import { useState, useEffect, useRef, useCallback } from "react";
import { debounce } from "lodash";
import { useInspectionAttributes } from "../../../hooks/query";

type ActionType = "R&R" | "Remove" | "Install" | "D&R";

interface IUseInspectionAttribute {
    project: any;
    attributes: any[];
    setAttributes: (e: any) => void;
}

export default function useInspectionAttribute(props: IUseInspectionAttribute) {
    const { project, attributes, setAttributes } = props;
    const { useUpdateSingleAttribute } = useInspectionAttributes();
    const updateAttributeSvc = useUpdateSingleAttribute();

    // --- Visual measurement state ---
    const [visualData, setVisualData] = useState<any[]>([]);
    const [actions, setActions] = useState<Record<string, ActionType>>({
        "Roof Vent": "R&R",
        "Pipe Jack": "R&R",
        "Split Boots": "R&R",
        "Furnace Vents": "R&R",
        "Bathroom Vents": "R&R",
        "Dryer Vents": "R&R",
        Chimney: "R&R",
        Skylights: "R&R",
        "Step Flashing": "Install",
        "Kick-out Diverter": "R&R",
        "Ice & Water Barrier": "Install",
        "Cornice Returns": "R&R",
        "Cornice Strip": "R&R",
        Sheathing: "R&R",
        "Fascia Board": "R&R",
        Soffit: "R&R",
        "Gutter Apron": "R&R",
        "Gutter/Downspout": "R&R",
        "Paint Gutter": "Install",
        "Low Slope": "R&R",
        "Counter Flashing": "Install",
    });

    const visualItems = [
        "Roof Vent",
        "Pipe Jack",
        "Furnace Vents",
        "Bathroom Vents",
        "Dryer Vents",
        "Split Boots",
        "Chimney",
        "Skylights",
        "High Roof",
        "Ice & Water Barrier",
        "Step Flashing",
        "Kick-out Diverter",
        "Low Slope",
        "Counter Flashing",
        "Cornice Returns",
        "Cornice Strip",
        "Fascia Board",
        "Gutter Apron",
        "Sheathing",
        "Paint Gutter",
    ];

    const processVisualData = useCallback(() => {
        if (!attributes || attributes.length === 0) return;

        const newVisualData: any[] = [];
        const newActions: Record<string, ActionType> = {};

        visualItems.forEach((name) => {
            const attr = attributes.find((a: any) => a.AttributeName === name);
            if (attr) {
                newVisualData.push({
                    name,
                    actual: attr.Qty !== null ? Number(attr.Qty) : 0,
                    id: attr.Id,
                });
                if (attr.Action) {
                    newActions[name] = attr.Action as ActionType;
                }
            } else {
                newVisualData.push({ name, actual: 0, id: null });
            }
        });

        // Also process Ancillary items that might not be in visualItems but are on the page
        const ancillaryItems = [
            "Cornice Returns",
            "Cornice Strip",
            "Fascia Board",
            "Soffit",
            "Gutter Apron",
            "Sheathing",
            "Gutter/Downspout",
            "Paint Gutter",
        ];
        ancillaryItems.forEach((name) => {
            const attr = attributes.find((a: any) => a.AttributeName === name);
            if (attr && attr.Action) {
                newActions[name] = attr.Action as ActionType;
            }
        });

        setVisualData(newVisualData);
        setActions((prev) => ({ ...prev, ...newActions }));
    }, [attributes]);

    useEffect(() => {
        processVisualData();
    }, [processVisualData]);

    // ─── Debounced API sync (fires after 500ms of inactivity) ───────────────────
    // Accepts previousValue, previousQty, previousAction so it can revert on failure.
    const debouncedAttributeUpdate = useRef(
        debounce(
            async (params: {
                Id?: string;
                ProjectId?: string;
                AttributeName?: string;
                Value?: any;
                Qty?: number;
                Action?: string;
                previousValue?: any;
                previousQty?: number;
                previousAction?: string;
                isNewEntry?: boolean;
            }) => {
                const { previousValue, previousQty, previousAction, isNewEntry, ...apiParams } = params;
                try {
                    const response = await updateAttributeSvc.mutateAsync(apiParams);
                    const finalId = apiParams.Id || response?.Id;

                    // Sync the server-confirmed Id back into local state (important for new entries)
                    setAttributes((prev: any[]) => {
                        return prev.map((a: any) => {
                            if (
                                (apiParams.Id && a.Id === apiParams.Id) ||
                                a.AttributeName === apiParams.AttributeName
                            ) {
                                return { ...a, Id: finalId };
                            }
                            return a;
                        });
                    });
                } catch (error) {
                    console.error("[InspectionAttribute] API failed, reverting:", apiParams.AttributeName, error);

                    if (isNewEntry) {
                        // New attribute — remove the optimistically-added entry
                        setAttributes((prev: any[]) =>
                            prev.filter((a: any) => a.AttributeName !== apiParams.AttributeName),
                        );
                    } else if (apiParams.AttributeName !== undefined) {
                        // Existing attribute — revert to previous values
                        setAttributes((prev: any[]) =>
                            prev.map((a: any) => {
                                if (a.AttributeName === apiParams.AttributeName) {
                                    return {
                                        ...a,
                                        AttributeValue: previousValue !== undefined ? previousValue : a.AttributeValue,
                                        Qty: previousQty !== undefined ? previousQty : a.Qty,
                                        Action: previousAction !== undefined ? previousAction : a.Action,
                                    };
                                }
                                return a;
                            }),
                        );
                    }
                }
            },
            500,
        ),
    ).current;

    useEffect(() => () => debouncedAttributeUpdate.cancel(), [debouncedAttributeUpdate]);

    const handleVisualInput = (itemName: string, value: number) => {
        setVisualData((prev) => prev.map((item) => (item.name === itemName ? { ...item, actual: value } : item)));

        const attr = attributes?.find((a: any) => a.AttributeName === itemName);
        const previousQty = attr?.Qty !== null && attr?.Qty !== undefined ? Number(attr.Qty) : 0;
        // Prefer what's already saved in DB, then fall back to the visual actions state, then "R&R"
        const currentAction = attr?.Action || actions[itemName] || "R&R";
        const isNewEntry = !attr?.Id;

        // 1. Apply change to parent state immediately — also carry action forward to prevent stale reads
        setAttributes((prev: any[]) => {
            const exists = prev.some((a: any) => a.AttributeName === itemName);
            if (!exists) {
                return [...prev, { Id: null, AttributeName: itemName, Qty: value, Action: currentAction }];
            }
            return prev.map((a: any) =>
                a.AttributeName === itemName ? { ...a, Qty: value, Action: currentAction } : a,
            );
        });

        // 2. Sync to backend (debounced), carry rollback context
        debouncedAttributeUpdate({
            Id: attr?.Id,
            ProjectId: isNewEntry ? project?.id : undefined,
            AttributeName: isNewEntry ? itemName : undefined,
            Qty: value,
            Action: currentAction,
            previousQty,
            previousAction: currentAction,
            isNewEntry,
        });
    };

    const handleActionChange = (itemName: string, newAction: ActionType) => {
        setActions((prev) => ({ ...prev, [itemName]: newAction }));

        const attr = attributes?.find((a: any) => a.AttributeName === itemName);
        const qty = visualData.find((v) => v.name === itemName)?.actual || 0;
        const previousQty = attr?.Qty !== null && attr?.Qty !== undefined ? Number(attr.Qty) : qty;
        const previousAction = attr?.Action || actions[itemName] || "R&R";
        const isNewEntry = !attr?.Id;

        // 1. Apply change to parent state immediately — UI responds instantly and keeps state in sync
        setAttributes((prev: any[]) => {
            const exists = prev.some((a: any) => a.AttributeName === itemName);
            if (!exists) {
                return [...prev, { Id: null, AttributeName: itemName, Qty: qty, Action: newAction }];
            }
            return prev.map((a: any) =>
                a.AttributeName === itemName ? { ...a, Action: newAction } : a,
            );
        });

        // 2. Sync to backend (debounced), carry rollback context
        debouncedAttributeUpdate({
            Id: attr?.Id,
            ProjectId: isNewEntry ? project?.id : undefined,
            AttributeName: isNewEntry ? itemName : undefined,
            Qty: qty,
            Action: newAction,
            previousQty,
            previousAction,
            isNewEntry,
        });
    };

    const getAttrValue = (name: string) => attributes?.find((attr: any) => attr.AttributeName === name)?.AttributeValue;

    // ─── Optimistic update: instant UI, debounced API, rollback on failure ──────
    const handleUpdate = (value: string, attributeName: string) => {
        const attr = attributes?.find((a: any) => a.AttributeName === attributeName);
        const previousValue = attr?.AttributeValue;
        const isNewEntry = !attr?.Id;

        // 1. Apply change to local state immediately — UI responds instantly
        setAttributes((prev: any[]) => {
            const exists = prev.some((a: any) => a.AttributeName === attributeName);
            if (!exists) {
                return [...prev, { Id: null, AttributeName: attributeName, AttributeValue: value }];
            }
            return prev.map((a: any) =>
                a.AttributeName === attributeName ? { ...a, AttributeValue: value } : a,
            );
        });

        // 2. Sync to backend (debounced), carry rollback context
        debouncedAttributeUpdate({
            Id: attr?.Id,
            ProjectId: isNewEntry ? project?.id : undefined,
            AttributeName: isNewEntry ? attributeName : undefined,
            Value: value,
            previousValue,
            isNewEntry,
        });
    };

    const getVisualItem = (name: string) => visualData.find((v) => v.name === name);

    return {
        visualData,
        actions,
        handleVisualInput,
        handleActionChange,
        handleUpdate,
        getAttrValue,
        getVisualItem,
    };
}
